import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/auth-client";
import { SubscriptionCard } from "@/components/billing/subscription-card";
import { UsageCard } from "@/components/billing/usage-card";
import { PricingGrid } from "@/components/pricing/pricing-grid";
import { BillingSkeleton } from "@/components/skeletons";
import { CREDIT_ALLOWANCES, type Tier } from "@/lib/stripe-config";
import { useState } from "react";
import { toast } from "sonner";
import {
  getServerCreditBalance,
  getServerSubscriptionDetails,
} from "@/lib/server-data";
import type { SubscriptionData } from "@/types/billing";

// Type guard to ensure tier is valid for CREDIT_ALLOWANCES lookup
const isValidTier = (tier: unknown): tier is keyof typeof CREDIT_ALLOWANCES => {
  return typeof tier === "string" && tier in CREDIT_ALLOWANCES;
};

export const Route = createFileRoute("/_authenticated/billing")({
  // No loader - all data fetched client-side
  component: BillingPage,
});

function BillingPage() {
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { session, region } = Route.useRouteContext();

  // Fetch credits client-side
  const { data: balance, isPending: balanceLoading } = useQuery({
    queryKey: ["credits", session?.user?.id],
    queryFn: getServerCreditBalance,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch subscription client-side
  const { data: subscription, isPending: subLoading } = useQuery({
    queryKey: ["subscription", session?.user?.id],
    queryFn: () =>
      getServerSubscriptionDetails({
        data: { userId: session!.user.id },
      }),
    staleTime: 5 * 60 * 1000,
  });

  // Show skeleton during loading
  if (balanceLoading || subLoading) {
    return <BillingSkeleton />;
  }

  const subscriptionData: SubscriptionData | null = subscription ?? null;

  const handleUpgrade = async (
    priceId: string,
    tier: Tier,
    planName: string,
  ) => {
    setLoadingTier(tier);
    try {
      // Use Better Auth's built-in subscription upgrade
      const response = await authClient.subscription.upgrade({
        plan: planName,
        successUrl: `${window.location.origin}/dashboard?checkout=success`,
        cancelUrl: `${window.location.origin}/billing?checkout=canceled`,
      });

      if (response?.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout failed:", error);
      setLoadingTier(null);
    }
  };

  const handleRestore = async (subscriptionId: string, tier: Tier) => {
    setLoadingTier(tier);
    try {
      const response = await authClient.subscription.restore({
        subscriptionId,
      });

      if (response?.error) {
        throw new Error(response.error.message);
      }

      toast.success("Subscription reactivated successfully!");
      // Invalidate queries to refetch fresh data
      await queryClient.invalidateQueries({
        queryKey: ["subscription", session?.user?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["credits", session?.user?.id],
      });
      setLoadingTier(null);
    } catch (error) {
      console.error("Failed to restore subscription:", error);
      toast.error("Failed to reactivate subscription");
      setLoadingTier(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      // Use Better Auth's built-in portal method
      const response = await authClient.subscription.billingPortal({
        returnUrl: `${window.location.origin}/billing`,
      });

      if (response?.data?.url) {
        window.location.href = response.data.url;
      } else if (response?.error) {
        throw new Error(response.error.message);
      }
    } catch (error) {
      console.error("Failed to open customer portal:", error);
      toast.error("Failed to open customer portal");
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await authClient.subscription.cancel({
        returnUrl: `${window.location.origin}/billing`,
      });
      // Invalidate queries to refetch fresh data
      await queryClient.invalidateQueries({
        queryKey: ["subscription", session?.user?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["credits", session?.user?.id],
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="bg-background min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm">
              ← Back to Dashboard
            </Button>
          </Link>
        </div>

        <div className="mx-auto max-w-4xl space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Billing & Subscription</h1>
            <p className="text-muted-foreground">
              Manage your subscription and billing information
            </p>
          </div>

          {/* Current Subscription */}
          <SubscriptionCard
            subscription={subscriptionData}
            onManage={handleManageBilling}
            onCancel={handleCancelSubscription}
          />

          {/* Usage */}
          {balance &&
            typeof balance === "object" &&
            "credits" in balance &&
            "tier" in balance &&
            typeof balance.credits === "number" &&
            isValidTier(balance.tier) && (
              <UsageCard
                credits={balance.credits}
                tier={balance.tier}
                allowance={CREDIT_ALLOWANCES[balance.tier]}
              />
            )}

          {/* Pricing Grid */}
          <div>
            <h2 className="text-2xl font-bold mb-4">
              {subscriptionData ? "Change Plan" : "Choose Your Plan"}
            </h2>
            <PricingGrid
              region={region}
              showFreeTier={!subscriptionData}
              onUpgrade={handleUpgrade}
              onRestore={handleRestore}
              loadingTier={loadingTier}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
